import { FC, useCallback, useMemo, useState } from "react"
import cx from "classnames"
import Table from "@/components/Table"
import { RiDeleteBin6Line as BinIcon } from "react-icons/ri"
import { MdOutlineModeEdit as EditIcon } from "react-icons/md"
import AddEditCookieModal from "../AddEditCookieModal"
import DeleteCookieModal from "../DeleteCookieModal"
import Switch from "@/components/Switch"
import { useBreakpoints } from "@/hooks/useBreakpoints"
import { useEditAdminCookie } from "../../hooks/useEditAdminCookie"
import { useGetSystemValuesQuery } from "@/redux/services/generalApi"
import { DATE_FORMAT_OPTIONS } from "@/constants"
import type { TablePaginationProps } from "@/redux/services/types"
import type { ColumnsType } from "antd/es/table"
import type { AdminCookie } from "@/types/entities/cookie"

type Props = {
  className?: string
  cookies: AdminCookie[]
} & TablePaginationProps<TableDataType>

type TableDataType = {
  key: string
  enabled: boolean
  name: string
  region: string
  updatedAt: string
}

const AdminCookieTable: FC<Props> = ({
  className,
  cookies,
  onRowSelect,
  selectedRowKeys,
  ...rest
}: Props) => {
  const { sm } = useBreakpoints()
  const [editedCookieId, setEditedCookieId] = useState("")
  const [deletedCookieId, setDeletedCookieId] = useState("")
  const { editAdminCookie } = useEditAdminCookie()
  const { data: { regions } = { regions: [] } } = useGetSystemValuesQuery()
  const handleUpdateStatus = useCallback(
    (id: string) => async (checked: boolean) => {
      await editAdminCookie({
        body: {
          enabled: checked,
        },
        id,
      })
    },
    [editAdminCookie],
  )
  const dataSource = useMemo<TableDataType[]>(
    () =>
      cookies.map(({ _id, name, region, updatedAt, enabled }) => ({
        key: _id,
        enabled,
        name,
        region,
        updatedAt: updatedAt
          ? new Date(updatedAt).toLocaleDateString("en-US", DATE_FORMAT_OPTIONS)
          : "",
      })),
    [cookies],
  )
  const columns = useMemo<ColumnsType<TableDataType>>(
    () => [
      {
        title: "",
        key: "enabled",
        dataIndex: "enabled",
        render: (enabled, { key }) => (
          <Switch checked={enabled} onChange={handleUpdateStatus(key)} />
        ),
        width: 70,
      },
      {
        title: "Cookie Name",
        key: "name",
        dataIndex: "name",
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: "Region",
        key: "region",
        dataIndex: "region",
        sorter: (a, b) => a.region.localeCompare(b.region),
        render: (v: string) => regions.find(({ id }) => id === v)?.name ?? "",
      },
      {
        title: "Last Update",
        key: "updatedAt",
        dataIndex: "updatedAt",
        sorter: (a, b) => +a.updatedAt - +b.updatedAt,
      },
      {
        title: "",
        dataIndex: "actions",
        key: "actions",
        className: "min-w-[96px]",
        fixed: sm ? "right" : false,
        render: (_, { key }) => (
          <div className="actions hidden gap-4">
            <EditIcon
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                setEditedCookieId(key)
              }}
              className="w-6 h-6 opacity-60 active:scale-90 cursor-pointer hover:text-primary transition-colors"
            />
            <BinIcon
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                setDeletedCookieId(key)
              }}
              className="w-6 h-6 opacity-60 active:scale-90 cursor-pointer hover:text-primary transition-colors"
            />
          </div>
        ),
        width: 96,
      },
    ],
    [handleUpdateStatus, regions, sm],
  )
  return (
    <>
      <Table
        dataSource={dataSource}
        columns={columns}
        rootClassName="w-full"
        className={cx("", className)}
        rowSelection={{
          type: "checkbox",
          onChange: (selectedRowKeys) =>
            onRowSelect && onRowSelect(selectedRowKeys),
          selectedRowKeys,
        }}
        showSizeChanger
        {...rest}
      ></Table>
      <AddEditCookieModal
        data={cookies.find(({ _id }) => _id === editedCookieId)}
        open={!!editedCookieId}
        onClose={() => setEditedCookieId("")}
      />
      <DeleteCookieModal
        open={!!deletedCookieId}
        data={[cookies.find(({ _id }) => _id === deletedCookieId)!].filter(
          Boolean,
        )}
        onClose={() => setDeletedCookieId("")}
      />
    </>
  )
}

export default AdminCookieTable
